const { compose, scale: mScale, translate: mTranslate } = window.TransformationMatrix;

import { PointDragger, TouchDragger, canvasToDom } from "./utils.js";
import { Widget } from "./widget.js";
import { AddEmoteDialog } from "./widgets-emotes.js";

class Toolbar extends Widget
{
    constructor({ editor, remixDocument, ...rest })
    {
        super({
            html: `
    <div class="toolbar">
    </div>
`, ...rest});
        this.root.addEventListener("click", (e) => this.onClick(e), { signal: this._signal });
        this.editor = editor;

        this.buttons = [{
            action: "flip",
            icon: "flip",
            enabled: () => remixDocument.selectedLayer != null,
        }, {
            action: "clone",
            icon: "content_copy",
            enabled: () => remixDocument.selectedLayer != null,
        }, {
            action: "toggleClip",
            icon: "tab_inactive",
            enabled: () => remixDocument.selectedLayer != null,
            selected: () => editor.editingClip,
        }, {
            separator: true,
        }, {
            action: "undo",
            icon: "undo",
            enabled: () => remixDocument.canUndo,
        }, {
            action: "redo",
            icon: "redo",
            enabled: () => remixDocument.canRedo,
        }, {
            action: "delete",
            icon: "delete",
            enabled: () => remixDocument.selectedLayer != null && !remixDocument.selectedLayer.fixed,
        }, {
            action: "copy",
            icon: "content_copy",
        }, {
            action: "add",
            icon: "add_circle",
        }];

        let toolbarNode = this.root;
        for(let buttonInfo of this.buttons)
        {
            if(buttonInfo.separator)
            {
                let sep = document.createElement("div");
                sep.classList.add("toolbar-separator");
                toolbarNode.appendChild(sep);
                continue;
            }

            let button = document.createElement("button");
            button.classList.add("toolbar-button");
            button.classList.add("material-symbols-outlined");
            button.dataset.action = buttonInfo.action;
            button.textContent = buttonInfo.icon;
            toolbarNode.appendChild(button);
        }

        this.remixDocument = remixDocument;
        for(let eventName of ["refresh", "undochanged", "selectionchanged"])
            this.remixDocument.addEventListener(eventName, () => this.refresh(), { signal: this._signal });

        this.refresh();
    }

    refresh()
    {
        for(let buttonInfo of this.buttons)
        {
            let button = this.root.querySelector(`.toolbar-button[data-action="${buttonInfo.action}"]`);
            let enabled = buttonInfo.enabled? buttonInfo.enabled(): true;
            if(buttonInfo.selected)
                button.classList.toggle("selected", enabled && buttonInfo.selected());

            if(buttonInfo.enabled)
            {
                if(enabled)
                    button.removeAttribute("disabled");
                else
                    button.setAttribute("disabled", "disabled");
            }
        }
    }

    onClick(e)
    {
        let button = e.target.closest(".toolbar-button");
        if(!button)
            return;

        let action = button.dataset.action;
        switch(action)
        {
        case "flip":
        {
            let layer = this.remixDocument.selectedLayer;
            if(!layer)
                return;

            this.remixDocument.pushUndo();

            let { width } = layer.unscaledSize;
            let m = compose(
                mTranslate(width, 0),
                mScale(-1, 1),
            );
            layer.matrix = compose(layer.matrix, m);
            layer.fireRefresh();
            break;
        }
        case "toggleClip":
            this.editor.editingClip = !this.editor.editingClip;
            break;

        case "clone":
        {
            let layer = this.remixDocument.selectedLayer;
            if(!layer)
                return;

            this.remixDocument.pushUndo();

            let newLayer = layer.clone();
            newLayer.matrix = compose(
                mTranslate(50, 0),
                layer.matrix,
            );
            this.remixDocument.addLayer(newLayer);
            this.remixDocument.selectedLayer = newLayer;
            break;
        }
        case "undo":
            this.remixDocument.undo();
            break;
        case "redo":
            this.remixDocument.redo();
            break;
        case "delete":
            let layer = this.remixDocument.selectedLayer;
            if(layer && !layer.fixed)
                this.remixDocument.removeLayer(layer);
            break;
        case "copy":
            this.remixDocument.copyToClipboard();
            break;
        case "add":
            this.addEmoteDialog = new AddEmoteDialog({
                remixDocument: this.remixDocument,
                parentNode: document.documentElement,
            });
            break;
        }
    }
}

class DesktopOverlay extends Widget
{
    constructor({
        editor,
        remixDocument,
        canvas,
        ...rest
    })
    {
        // The controls layer is positioned to align with the canvas.
        super({
            html: `
    <div class=desktop-overlay>
        <div class=drag-handle data-pos=0></div>
        <div class=drag-handle data-pos=1></div>
        <div class=drag-handle data-pos=2></div>
        <div class=drag-handle data-pos=3></div>
    </div>
`, ...rest});

        this.editor = editor;
        this.remixDocument = remixDocument;
        this._scaleMin = 0.1;
        this._scaleMax = 5.0;
        this.canvas = canvas;
        this.handles = this.root.querySelectorAll(".drag-handle");
        console.assert(this.handles.length === 4);
        this._displayed = true;

        for(let eventName of ["refresh", "undochanged", "selectionchanged"])
            this.remixDocument.addEventListener(eventName, () => this.refresh(), { signal: this._signal });

        for(let handle of this.handles)
        {
            handle.addEventListener("pointerdown", (e) => {
                console.log("Handle pointerdown", e);

                if(this._canvasDragger)
                {
                    console.log("Already dragging a handle");
                    return;
                }

                let layer = this.remixDocument.selectedLayer;
                if(!layer)
                {
                    console.log("No selected layer");
                    return;
                }

                this._draggingLayer = layer;
                this._draggingLayerInitialTransform = this.editor.editingClip? this._draggingLayer.worldClipMatrix: this._draggingLayer.matrix;
                this._savedUndo = false;
        
                // Get the center of the layer in client coordinates.  This will be the
                // center of the transform.
                let centerPos = layer.center;
                let { clientLeft, clientTop } = canvasToDom(this.canvas, centerPos);
                centerPos = { x: clientLeft, y: clientTop };

                this._canvasDragger = new PointDragger(e.target, {
                    signal: this._signal,
                    canvas: this.canvas,
                    centerPos,
                    pointerEvent: e,
                    onmove: ({ matrix }) =>
                    {
                        // Save undo on the first change.
                        if(!this._savedUndo)
                        {
                            this.remixDocument.pushUndo();
                            this._savedUndo = true;
                        }

                        let newMatrix = compose(matrix, this._draggingLayerInitialTransform);
                        if(this.editor.editingClip)
                            this._draggingLayer.worldClipMatrix = newMatrix;
                        else
                            this._draggingLayer.matrix = newMatrix;
                        this._draggingLayer.fireRefresh();
                    },

                    onend: () =>
                    {
                        console.log("DesktopOverlay ondragend");
                        this._canvasDragger = null;
                    },
                });
            }, { signal: this._signal });
        }

        this.refresh();
    }

    get displayed() { return this._displayed; }
    set displayed(v) { this._displayed = v; this.refresh(); }

    refresh()
    {
        if(this._updating)
            return;
        
        let layer = this.remixDocument.selectedLayer;
        this.root.hidden = !this._displayed || !layer;
        if(this.root.hidden)
            return;

        this.root.classList.toggle("editing-clip", this.editor.editingClip);
        
        // Position the handles at the layer corners.
        let points = layer.corners({ forClip: this.editor.editingClip });
        for(let handleIdx = 0; handleIdx < 4; handleIdx++)
        {
            let handle = this.handles[handleIdx];
            let p = points[handleIdx];
            let { x, y } = canvasToDom(this.canvas, p);

            handle.style.left = `${x}px`;
            handle.style.top = `${y}px`;
        }
    }
}

export default class RemixEditor extends EventTarget
{
    constructor({ remixDocument })
    {
        super();

        this._abortController = new AbortController();
        this.signal = this._abortController.signal;
        let signal = this.signal;

        this.container = new Widget({
            parentNode: document.body,
            signal,
            html: `
<div class=editor>
    <div class=canvas-container>
        <container class=controls-container></container>
        <canvas></canvas>
    </div>
    <div class=toolbar-container></div>

    <container class=emote-list-container></container>
</div>
`});
        this.canvas = this.container.root.querySelector("canvas");
        this.container.root.style.setProperty("--ratio", remixDocument.width / remixDocument.height);
        this.context = this.canvas.getContext("2d");
        this._editingClip = false;

        this.toolbar = new Toolbar({
            editor: this,
            parentNode: this.container.root.querySelector(".toolbar-container"),
            remixDocument,
        });

        this.canvas.width = remixDocument.width;
        this.canvas.height = remixDocument.height;

        this.controlsNode = this.container.root.querySelector(".controls");
        this.container.root.addEventListener("contextmenu", (e) => e.preventDefault(), { signal, capture: true, passive: false });

        this._canvasDragger = new TouchDragger(this.canvas, {
            signal,
            canvas: this.canvas,
            onstart: (...params) => this.ondragstart(...params),
            onmove: (...params) => this.ondragmove(...params),
            onend: (...params) => this.ondragend(...params),
        });

        this.document = remixDocument;
        this.document.addEventListener("refresh", () => this.refresh(), { signal });

        this._desktopOverlay = new DesktopOverlay({
            editor: this,
            parentNode: this.container.root.querySelector(".controls-container"),
            remixDocument: this.document,
            canvas: this.canvas,
        });

        // XXX: guess initial value
        this._lastInputWasMouse = false;
        window.addEventListener("pointerdown", (e) => {
            let mouse = e.pointerType == "mouse";
            if(mouse == this._lastInputWasMouse)
                return;

            this._lastInputWasMouse = mouse;
            this.refresh();
        }, { capture: true, signal });

        this.refresh();
    }

    get editingClip() { return this._editingClip; }
    set editingClip(value)
    {
        this._editingClip = value;
        this.document.fireRefresh();
    }

    // Convert client coordinates to canvas coordinates.
    clientToCanvasPos({ clientX, clientY })
    {
        let rect = this.canvas.getBoundingClientRect();
        let pos = { x: clientX - rect.left, y: clientY - rect.top };

        // Scale from the DOM size to the canvas size:
        pos.x *= this.canvas.width / rect.width;
        pos.y *= this.canvas.height / rect.height;

        return pos;
    }

    refresh()
    {
        this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);

        this.document.draw({
            canvas: this.canvas,
            context: this.context,
            drawClipBorder: this._editingClip,
        });

        let shouldShowDesktopSliders = this._lastInputWasMouse && this.document.selectedLayer != null;
        this._desktopOverlay.displayed = shouldShowDesktopSliders;
    }

    shutdown()
    {
        this._abortController.abort();
        this.container.root.remove();
    }

    ondragstart({ initialEvent, clientX, clientY })
    {
        if(this._draggingLayer != null)
            throw new Error("Already dragging");

        this.document.selectedLayer = null;

        let pos = this.clientToCanvasPos({ clientX, clientY });
        let layer = this.document.intersectingLayer(pos.x, pos.y);
        if(!layer)
            return;

        this.document.selectedLayer = layer;

        // Keep track of whether we changed anything.
        this._dragChangedDocument = false;
        this.document.pushUndo();

        if(this.document.moveLayerToTop(layer))
            this._dragChangedDocument = true;

        this._draggingLayer = layer;
        this._draggingLayerInitialTransform = this.editingClip? this._draggingLayer.worldClipMatrix: this._draggingLayer.matrix;

        this.refresh();
    }

    ondragmove({ matrix })
    {
        if(this._draggingLayer == null)
            return;

        this._dragChangedDocument = true;

        let newMatrix = compose(matrix, this._draggingLayerInitialTransform);
        if(this.editingClip)
            this._draggingLayer.worldClipMatrix = newMatrix;
        else
            this._draggingLayer.matrix = newMatrix;
        this._draggingLayer.fireRefresh();
    }

    ondragend()
    {
        if(this._draggingLayer == null)
            return;

        this._draggingLayer = null;
        if(!this._dragChangedDocument)
            this.document.popUndo();
    }
}
